
import type React from "react"
import { useState } from "react"
import { BiBell, BiChevronDown, BiChevronRight, BiPhone, BiSearch, BiSend, BiX, BiMessageSquareDetail } from "react-icons/bi"
import { CiSettings } from "react-icons/ci"
import { FaBullhorn, FaEye, FaUsers } from "react-icons/fa6"
import { FiAlertTriangle } from "react-icons/fi"

// Custom Button Component
const Button = ({
  children,
  variant = "default",
  size = "default",
  className = "",
  onClick,
  ...props
}: {
  children: React.ReactNode
  variant?: "default" | "ghost" | "destructive" | "secondary"
  size?: "default" | "sm"
  className?: string
  onClick?: () => void
  [key: string]: any
}) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"

  const variants = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    destructive: "bg-red-500 text-white hover:bg-red-600",
    secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200",
  }

  const sizes = {
    default: "h-10 py-2 px-4",
    sm: "h-9 px-3 text-sm",
  }

  return (
    <button className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`} onClick={onClick} {...props}>
      {children}
    </button>
  )
}

// Custom Input Component
const Input = ({
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    className={`px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${className}`}
    {...props}
  />
);

// Custom Card Components
const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return <div className={`rounded-lg border bg-card text-card-foreground shadow-sm ${className}`}>{children}</div>
}

const CardHeader = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return <div className={`flex flex-col space-y-1.5 p-6 ${className}`}>{children}</div>
}

const CardTitle = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return <h3 className={`text-2xl font-semibold leading-none tracking-tight ${className}`}>{children}</h3>
}

const CardContent = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return <div className={`p-6 pt-0 ${className}`}>{children}</div>
}

// Custom Badge Component
const Badge = ({
  children,
  variant = "default",
  className = "",
}: {
  children: React.ReactNode;
  variant?: "default" | "secondary" | "destructive";
  className?: string;
}) => {
  const variants = {
    default: "bg-primary text-primary-foreground",
    secondary: "bg-secondary text-secondary-foreground",
    destructive:
      "bg-[#EA1919] text-white",
  };

  return (
    <div
      className={`inline-flex items-center rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${variants[variant]} ${className}`}
    >
      {children}
    </div>
  );
};

export default function IssuesCommunicationPage() {
  const [currentView, setCurrentView] = useState<"issues" | "communication">("issues")

  const [showMessageModal, setShowMessageModal] = useState(false)
  const [showIssueModal, setShowIssueModal] = useState(false)
//   const [showPayoutModal, setShowPayoutModal] = useState(false)
  const [selectedMessage, setSelectedMessage] = useState<any>(null)
  const [selectedIssue, setSelectedIssue] = useState<any>(null)
//   const [selectedPayout, setSelectedPayout] = useState<any>(null)
  const [showDropdown, setShowDropdown] = useState(false)

  const handleViewChange = (view: "issues" | "communication") => {
    setCurrentView(view)
  }

  const handleMessageDetails = (message: any) => {
    setSelectedMessage(message)
    setShowMessageModal(true)
  }

  const handleIssueDetails = (issue: any) => {
    setSelectedIssue(issue)
    setShowIssueModal(true)
  }

//   const getStatusBadge = (status: string) => {
//     const statusStyles = {
//       Sent: "bg-green-100 text-green-800",
//       Delivered: "bg-green-100 text-green-800",
//       "High Priority": "bg-red-100 text-red-800",
//       "In Progress": "bg-yellow-100 text-yellow-800",
//       Pending: "bg-yellow-100 text-yellow-800",
//       Processing: "bg-blue-100 text-blue-800",
//       Paid: "bg-green-100 text-green-800",
//     }
//     return statusStyles[status as keyof typeof statusStyles] || "bg-gray-100 text-gray-800"
//   }

  const renderIssuesContent = () => (
    <>
      {/* Issues Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="flex flex-col border-l-4 border-l-green-600">
          <CardHeader className="flex flex-row items-start space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Open Issues</CardTitle>
          </CardHeader>
          <CardContent className="mt-auto">
            <div className="text-2xl font-bold">2</div>
          </CardContent>
        </Card>

        <Card className="flex flex-col border-l-4 border-l-yellow-500">
          <CardHeader className="flex flex-row items-start space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">In Progress</CardTitle>
          </CardHeader>
          <CardContent className="mt-auto">
            <div className="text-2xl font-bold">2</div>
          </CardContent>
        </Card>

        <Card className="flex flex-col border-l-4 border-l-green-600">
          <CardHeader className="flex flex-row items-start space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Resolved Today</CardTitle>
          </CardHeader>
          <CardContent className="mt-auto">
            <div className="text-2xl font-bold">3</div>
          </CardContent>
        </Card>

        <Card className="flex flex-col border-l-4 border-l-red-500">
          <CardHeader className="flex flex-row items-start space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">High Priority</CardTitle>
          </CardHeader>
          <CardContent className="mt-auto">
            <div className="text-2xl font-bold">1</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-x-6 justify-between mb-6">
       <div className="relative w-full">
          <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input placeholder="Search Rider by name or payout ID" className="pl-10 h-10 w-[700px]" 
          />
        </div>
        <div className="flex items-center gap-2">
          <select
          className="h-10 rounded-md border border-input px-3 py-2 text-sm"
        >
          <option>All Statuses</option>
          <option>In Progress</option>
          <option>Resolved</option>
          <option>Closed</option>
        </select>
      </div>
        
      </div>

      {/* Issues List */}
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium">ISS-001</span>
                  <span className="text-green-600">•</span>
                  <span className="text-sm text-gray-600">Late Delivery</span>
                </div>
                <p className="text-sm text-gray-500">John Doe (Customer) • Delivery Time</p>
                <p className="text-xs text-green-600">8/19/2024</p>
              </div>
              <div className="flex items-center gap-x-10">
                <Badge className="bg-red-100 text-red-800">High Priority</Badge>
                 <Button
                  variant="ghost"
                  size="sm"
                  className="text-green-600 rounded-lg border border-[#000000BF] text-[#0000008C] space-x-2"
                 onClick={() =>
                    handleIssueDetails({
                      id: "ISS-001",
                      title: "Late delivery complaint",
                      type: "Customer Complaint (Delivery Time)",
                      reporter: "Mary John",
                      phone: "+234 567 867 9089",
                      order: "ORD-2415",
                      notes: "Rider was stuck in traffic. Customer offered compensation.",
                      status: "High Priority",
                    })
                  }
                >
                 <span>
                  View
                  </span> <FaEye className="w-4 h-4" />
                </Button>
                
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium">ISS-001</span>
                  <span className="text-green-600">•</span>
                  <span className="text-sm text-gray-600">App not working properly</span>
                </div>
                <p className="text-sm text-gray-500">John Doe (Rider) • Technical</p>
                <p className="text-xs text-green-600">8/19/2024</p>
              </div>
              <div className="flex items-center gap-x-10">
                <Badge className="bg-yellow-100 text-yellow-800">In Progress</Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-green-600 rounded-lg border border-[#000000BF] text-[#0000008C] space-x-2"
                 onClick={() =>
                    handleIssueDetails({
                      id: "ISS-001",
                      title: "Late delivery complaint",
                      type: "Customer Complaint (Delivery Time)",
                      reporter: "Mary John",
                      phone: "+234 567 867 9089",
                      order: "ORD-2415",
                      notes: "Rider was stuck in traffic. Customer offered compensation.",
                      status: "High Priority",
                    })
                  }
                >
                 <span>
                  View
                  </span> <FaEye className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )

  const renderCommunicationContent = () => (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compose Message */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Compose Message</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Message Type</label>
                <select className="w-full p-2 border border-gray-300 rounded-md">
                  <option>Broadcast to All</option>
                  <option>Targeted Message</option>
                  <option>Individual Message</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Subject</label>
                <Input className="w-full" placeholder="Enter Message Subject..." />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Message</label>
                <textarea
                  className="focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent w-full p-3 border border-gray-300 rounded-md h-32 resize-none"
                  placeholder="Enter Your message here"
                />
              </div>

              <Button className="bg-green-600 text-white hover:bg-green-700">
                <BiSend className="w-4 h-4 mr-2" />
                Send Message
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions & Stats */}
        <div className="h-full space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col space-y-3">
              <div className="flex w-full items-center justify-start text-gray-700">
                <BiBell className="w-4 h-4 mr-3" />
                <span>
                    Quick Weather Updates
                    </span>
              </div>
              <div className="flex w-full items-center justify-start text-gray-700">
                <FaBullhorn className="w-4 h-4 mr-3" />
                <span>
                Announce System Maintenance

                </span>
              </div>
              <div className="flex w-full items-center justify-start text-gray-700">
                <FaUsers className="w-4 h-4 mr-3" />
                <span>
                Notify Top Performers

                </span>
              </div>
              <div className="flex w-full items-center justify-start text-gray-700">
                <BiMessageSquareDetail className="w-4 h-4 mr-3" />
                <span>
                Send Policy Update

                </span>
              </div>

<div className="pt-7">
    <p className="text-lg">Communication Stats</p>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Messages sent:</span>
                <span className="font-medium">12</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Average Read Rate:</span>
                <span className="font-medium text-green-600">78%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Active Conversations:</span>
                <span className="font-medium">3</span>
              </div>
    </div>
            </CardContent>
          </Card>

        </div>
      </div>

      {/* Message History */}
      <div className="mt-8">
        <Card>
          <CardHeader>
            <CardTitle>Message History</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <CiSettings className="w-4 h-4 text-yellow-600" />
                </div>
                <div>
                  <p className="font-medium">System Maintenance Notice . Broadcast</p>
                  <p className="text-sm text-green-600">To: All Active Riders (247)</p>
                  <p className="text-xs text-gray-500">8/19/2024 By: Admin Read: 189/247</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">77% read</span>
                <Badge className="bg-green-100 text-green-800">Sent</Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#0000008C] border border-[#000000BF]"
                  onClick={() =>
                    handleMessageDetails({
                      title: "System Maintenance Notice",
                      type: "Broadcast",
                      recipients: "All Active Riders (247)",
                      sender: "Admin",
                      sentAt: "8/19/2024, 4:30:00 PM",
                      content:
                        "The app will be under maintenance tonight from 11 PM to 2 AM. Please complete all deliveries before 11 PM.",
                      readCount: 567,
                      unreadCount: 23,
                      readRate: "77%",
                    })
                  }
                >
                  View
                  <FaEye className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <FaUsers className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">Weekend Bonus Program. Targeted</p>
                  <p className="text-sm text-green-600">To: Top Performers</p>
                  <p className="text-xs text-gray-500">8/19/2024 By: Operations Manager Read: 42/45</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">77% read</span>
                <Badge className="bg-green-100 text-green-800">Sent</Badge>
                <Button variant="ghost" size="sm" className="text-[#0000008C] border border-[#000000BF]">
                  View
                     <FaEye className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <FaUsers className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">Performance Review . Individual</p>
                  <p className="text-sm text-green-600">To: Sarah Johnson</p>
                  <p className="text-xs text-gray-500">8/19/2024 By: Support Staff Read: 1/1</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">77% read</span>
                <Badge className="bg-green-100 text-green-800">Delivered</Badge>
                <Button variant="ghost" size="sm" className="text-[#0000008C] border border-[#000000BF]">
                  View
                     <FaEye className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <FiAlertTriangle className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <p className="font-medium">Weather Alert . Urgent</p>
                  <p className="text-sm text-green-600">To: All Active Gbagada Riders Riders (247)</p>
                  <p className="text-xs text-gray-500">8/19/2024 By: Operations Manager Read: 48/52</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">77% read</span>
                <Badge className="bg-green-100 text-green-800">Sent</Badge>
                <Button variant="ghost" size="sm" className=" text-[#0000008C] border border-[#000000BF]">
                  View
                     <FaEye className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )

  return (
    <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="mb-4 h-16 border-b border-gray-200 bg-white flex items-center justify-between">
          <div className="flex items-center justify-between">
            <div>
             <div className="relative flex items-center">
                <span className="text-lg font-semibold">
                    Logistics  
                </span>
               <BiChevronRight size={22} / >
            <Button
              variant="ghost"
              className="-ml-3 flex items-center gap-2 text-lg font-semibold"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              {currentView === "issues" ? "Issues" : "Communication Center"}
              <BiChevronDown className="w-4 h-4" />
            </Button>

            {showDropdown && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 min-w-[200px]">
                <button
                  className={`w-full px-4 py-2 text-left hover:bg-gray-50 ${
                    currentView === "communication" ? "bg-green-50 text-green-600" : ""
                  }`}
                  onClick={() => {
                    handleViewChange("communication")
                    setShowDropdown(false)
                  }}
                >
                  Communication Center
                </button>
                <button
                  className={`w-full px-4 py-2 text-left hover:bg-gray-50 ${
                    currentView === "issues" ? "bg-green-50 text-green-600" : ""
                  }`}
                  onClick={() => {
                    handleViewChange("issues")
                    setShowDropdown(false)
                  }}
                >
                  Issues
                </button>
              </div>
            )}
          </div>

              <p className="-mt-2 text-sm text-gray-500">
                Monitor vendor earnings, process payouts, and manage financial adjustments.
              </p>
            </div>
          </div>
        </header>

      {/* Content based on current view */}
      {currentView === "issues" ? renderIssuesContent() : renderCommunicationContent()}

      {/* Message Details Modal */}
      {showMessageModal && selectedMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-green-600">Message Details</h3>
                <p className="text-sm text-gray-500">
                  View detailed information about the broadcast message "{selectedMessage.title}" sent to{" "}
                  {selectedMessage.recipients}.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowMessageModal(false)}>
                <BiX className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <h4 className="text-lg font-medium text-yellow-600">{selectedMessage.title}</h4>
                <Badge className="bg-yellow-100 text-yellow-800">Broadcast</Badge>
              </div>

              <div>
                <h5 className="font-medium mb-2">Message Content</h5>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">{selectedMessage.content}</p>
                </div>
              </div>

              <div>
                <h5 className="font-medium mb-3">Delivery Details</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium">Recipients:</p>
                    <p className="text-sm text-gray-500">{selectedMessage.recipients}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Sent by:</p>
                    <p className="text-sm text-gray-500">{selectedMessage.sender}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Sent at:</p>
                    <p className="text-sm text-gray-500">{selectedMessage.sentAt}</p>
                  </div>
                </div>
              </div>

              <div>
                <h5 className="font-medium mb-3">Read Statistics</h5>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm font-medium">Read:</p>
                    <p className="text-lg font-bold text-green-600">{selectedMessage.readCount}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Unread:</p>
                    <p className="text-lg font-bold text-red-600">{selectedMessage.unreadCount}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Read Rate:</p>
                    <p className="text-lg font-bold">{selectedMessage.readRate}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Issue Details Modal */}
      {showIssueModal && selectedIssue && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-green-600">Issue Details - {selectedIssue.id}</h3>
                <p className="text-sm text-gray-500">
                  View and manage issue {selectedIssue.id} reported by {selectedIssue.reporter}.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowIssueModal(false)}>
                <BiX className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-medium">{selectedIssue.title}</h4>
                <div className="flex items-center gap-2 mt-1">
                  <FaUsers className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">{selectedIssue.type}</span>
                </div>
              </div>

              <div>
                <h5 className="font-medium mb-2">Description</h5>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">Customer complaint about delayed delivery time.</p>
                </div>
              </div>

              <div>
                <h5 className="font-medium mb-3">Reporter Information:</h5>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <FaUsers className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">{selectedIssue.reporter}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <BiPhone className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">{selectedIssue.phone}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-green-600">Order:</span>
                    <span className="text-sm">{selectedIssue.order}</span>
                  </div>
                </div>
              </div>

              <div>
                <h5 className="font-medium mb-2">Notes:</h5>
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-sm text-green-700">{selectedIssue.notes}</p>
                </div>
              </div>

              <div>
                <h5 className="font-medium mb-3">Add a Response:</h5>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-md h-24 resize-none"
                  placeholder="Type Your response and update"
                />
                <div className="flex items-center justify-between mt-3">
                  <select className="p-2 border border-gray-300 rounded-md">
                    <option>Status</option>
                    <option>Open</option>
                    <option>In Progress</option>
                    <option>Resolved</option>
                  </select>
                  <Button className="bg-green-600 text-white hover:bg-green-700">Update</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
